import React from "react"
import { useAsync } from "react-async-hook"
import getSDK from "@ua/web-sdk"

import { sdkBaseUrl } from "@root/globals"
import { AirshipWebPrompt } from "@ua/web-sdk/lib/plugins/html-prompt/types"
import {
  SubscriptionPluginForm,
  FormOptions,
  SubscriptionPluginModalForm,
  SubscriptionPluginModalOptions,
  SubscriptionFormFactory,
} from "@ua/web-sdk/lib/plugins/subscription-form/types"

enum Plugin {
  HtmlPrompt = "html-prompt",
  SubscriptionForm = "subscription-form",
}

type PluginMetaOptions = {
  loadMinified: boolean
}

type PromptType = "alert" | "bell"

interface PromptOptionsBase {
  UA?: string
  appearDelay?: number
  disappearDelay?: number
  askAgainDelay?: number
  stylesheet?: string
  auto?: boolean
  type: PromptType
  meta: PluginMetaOptions
}

interface HtmlPromptLanguageOptions {
  title?: string
  message?: string
  accept?: string
  deny?: string
}

interface AlertPromptOptions extends PromptOptionsBase {
  type: "alert"
  position?: "top" | "bottom"
  logo?: string
  i18n?: { [key: string]: HtmlPromptLanguageOptions }
}

interface BellPromptOptions extends PromptOptionsBase {
  type: "bell"
  position: "top-left" | "top-right" | "bottom-left" | "bottom-right"
}

type HtmlPromptOptions = AlertPromptOptions | BellPromptOptions

type SubscriptionFormOptions = {
  meta: PluginMetaOptions
}

interface Options {
  [Plugin.HtmlPrompt]: HtmlPromptOptions
  [Plugin.SubscriptionForm]: SubscriptionFormOptions
}

interface PluginInstance {
  [Plugin.HtmlPrompt]: AirshipWebPrompt
  [Plugin.SubscriptionForm]: SubscriptionFormFactory
}

const pluginPath: { [K in Plugin]: string } = {
  [Plugin.HtmlPrompt]: "ua-html-prompt",
  [Plugin.SubscriptionForm]: "ua-subscription-form",
}

interface PluginLoad<T> {
  loading: boolean
  error?: Error
  result?: T
}

async function loadPlugin<T extends Plugin>(
  plugin: T,
  { meta, ...options }: Options[T]
): Promise<PluginInstance[T]> {
  const extension = meta.loadMinified ? "min.js" : "js"
  const path = `${sdkBaseUrl}/${pluginPath[plugin]}.${extension}`

  const sdk = await getSDK()

  return (await sdk.plugins.load(plugin, path, options)) as PluginInstance[T]
}

function useSdkPlugin<T extends Plugin>(
  plugin: T,
  options: Options[T],
  deps: unknown[]
): PluginLoad<PluginInstance[T]> {
  return useAsync(() => loadPlugin(plugin, options), deps)
}

export function useHtmlPrompt(
  options: HtmlPromptOptions,
  deps: unknown[]
): PluginLoad<AirshipWebPrompt> {
  return useSdkPlugin(Plugin.HtmlPrompt, options, deps)
}

export function useSubscriptionForm(
  options: SubscriptionFormOptions,
  deps: unknown[]
): PluginLoad<SubscriptionFormFactory> {
  return useSdkPlugin(Plugin.SubscriptionForm, options, deps)
}

export function useSubscriptionModal(
  { meta, ...options }: SubscriptionPluginModalOptions & { meta: PluginMetaOptions },
  deps: unknown[]
): PluginLoad<SubscriptionPluginModalForm> {
  const { result, ...rest } = useSubscriptionForm({ meta }, deps)
  const [form, setForm] = React.useState<SubscriptionPluginModalForm>()

  React.useEffect(() => {
    if (result) {
      setForm(result.setupModalForm(options))
      return () => {
        form?.destroy()
        setForm(undefined)
      }
    }
  }, [result])

  return { ...rest, result: form }
}

export function useSubscriptionEmbed(
  el: HTMLElement,
  { meta, ...options }: FormOptions & { meta: PluginMetaOptions },
  deps: unknown[]
): PluginLoad<SubscriptionPluginForm> {
  const { result, ...rest } = useSubscriptionForm({ meta }, deps)
  const [form, setForm] = React.useState<SubscriptionPluginForm>()

  React.useEffect(() => {
    if (result) {
      setForm(result.embedForm(el, options))
      return () => {
        form?.destroy()
        setForm(undefined)
      }
    }
  }, [result])

  return { ...rest, result: form }
}
